/**
 * Save lead search bookmark — shared by LeadsToolbarV2 (bookmark icon) and LeadFilters (Save button).
 * Receives the filter object to persist (applied list filters from parent, or draft filters from the panel).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material';
import { BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { useLead } from '../../../contexts/LeadContext';
import { useModal } from '../../../contexts/ModalContext';
import { LEAD_THEME as T, toTitleCase } from '../../../utils/lead_helper_v2';

const textFieldStyles = {
  '& .MuiInputBase-root': {
    fontSize: '0.72rem',
    minHeight: '30px',
    fontWeight: 400,
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.72rem',
    fontWeight: 400,
  },
};

const PREVIEW_LABELS = {
  budget_ranges: 'Budget',
  locations: 'Location',
  property_types: 'Property Type',
  intent: 'Intent',
  agent_id: 'Agent',
  agents: 'Agent',
  assigned_to: 'Agent',
  source: 'Source',
  channel_partner: 'Channel Partner',
  channel_partners: 'Channel Partners',
  lead_urgency: 'Urgency',
  lead_lcp: 'LCP',
  tags: 'Tags',
  site_visit_frequency: 'Visit Frequency',
  last_contacted: 'Last Contacted',
  budget: 'Budget (Slider)',
};

const PRESET_LABELS = {
  '1hour': '1 hours',
  '6hours': '6 hours',
  '12hours': '12 hours',
  '24hours': '24 hours',
  custom: 'Custom Range',
};

function isNoiseBudgetSlider(b) {
  if (!b || typeof b !== 'object') return true;
  const min = b.min_value ?? b.min ?? 0;
  const max = b.max_value ?? b.max ?? 0;
  return min === 0 && max === 0;
}

function SaveSearchFilterPreview({ previewSrc, defaultFilters, agents = [] }) {
  if (!previewSrc || typeof previewSrc !== 'object') return null;

  const previewItems = [];
  Object.entries(previewSrc).forEach(([key, value]) => {
    if (!value || key === 'is_deleted') return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;

    if (
      key === 'budget' &&
      (previewSrc.budget_ranges?.length > 0 || isNoiseBudgetSlider(value))
    ) {
      return;
    }

    const label = PREVIEW_LABELS[key] || toTitleCase(key);
    let displayValues = '';

    if (key === 'last_contacted' && typeof value === 'object') {
      displayValues =
        PRESET_LABELS[value.preset] || value.preset || (value.start_date ? 'Custom' : '');
    } else if (key === 'budget' && typeof value === 'object') {
      const fmt = (n) =>
        n >= 10000000 ? `${(n / 10000000).toFixed(1)} Cr` : `${(n / 100000).toFixed(0)}L`;
      const lo = value.min_value ?? value.min ?? 0;
      const hi = value.max_value ?? value.max ?? 0;
      displayValues = `${fmt(lo)} – ${fmt(hi)}`;
    } else if (Array.isArray(value)) {
      // Agent keys → resolve from defaultFilters.agent_filters [{agent_id, agent_name}]
      const isAgentKey = ['agent_id', 'agents', 'assigned_to'].includes(key);
      // Channel partners (plural) → resolve from defaultFilters.channel_partner [{value, label}]
      const isCPKey = key === 'channel_partners';

      const agentList = defaultFilters?.agent_filters || [];
      const cpList = defaultFilters?.channel_partner || [];
      const opts = defaultFilters?.[key];

      displayValues = value
        .map((v) => {
          const raw = typeof v === 'object' && v !== null ? (v.agent_id ?? v.value ?? v.id ?? v) : v;

          if (isAgentKey) {
            // Search in both the passed agents list and defaultFilters.agent_filters
            const allAgents = [...(agents || []), ...(agentList || [])];
            const match = allAgents.find(
              (a) => String(a.agent_id ?? a.id) === String(raw),
            );
            if (match?.agent_name) return `${match.agent_name}${match.email ? ` (${match.email})` : ''}`;
          }

          if (isCPKey) {
            const match = cpList.find((c) => String(c.value) === String(raw));
            if (match?.label) return match.label;
          }

          if (Array.isArray(opts)) {
            const match = opts.find((o) => String(o?.value ?? o) === String(raw));
            if (match?.label) return match.label;
          } else if (typeof opts === 'object' && opts !== null) {
            if (opts[raw]) return opts[raw];
          }

          return typeof raw === 'string' ? raw.replace(/_/g, ' ') : String(raw);
        })
        .join(', ');
    } else {
      displayValues = String(value);
    }
    previewItems.push({ label, displayValues });
  });

  if (previewItems.length === 0) return null;

  return (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#F8FAFB', border: `1px solid ${T.border}` }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: T.ink2,
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Filters to be saved ({previewItems.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {previewItems.map(({ label: pLabel, displayValues: pValues }) => (
          <Box key={pLabel} sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.ink2, minWidth: 90, flexShrink: 0 }}>
              {pLabel}:
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.ink, lineHeight: 1.4, wordBreak: 'break-word' }}>
              {pValues}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const SaveLeadSearchDialog = ({ open, onClose, onSaved, filters, defaultFilters = {}, agents = [] }) => {
  const { createSavedSearch, setStatusCounts } = useLead();
  const { openSuccessModal } = useModal();
  const [searchName, setSearchName] = useState('');
  const [error, setError] = useState(null);
  const [savingSearch, setSavingSearch] = useState(false);

  useEffect(() => {
    if (open) {
      setSearchName('');
      setError(null);
    }
  }, [open]);

  const handleSave = useCallback(async () => {
    if (!searchName.trim()) {
      setError('Please enter a search name');
      return;
    }

    const nonEmptyFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(([_, value]) => !(Array.isArray(value) && value.length === 0)),
    );

    if (Object.keys(nonEmptyFilters).length === 0) {
      setError('At least one filter must be applied');
      return;
    }

    for (const [key, value] of Object.entries(nonEmptyFilters)) {
      if (Array.isArray(value) && value.length === 0) {
        setError(`${toTitleCase(key)} filter cannot be empty`);
        return;
      }
    }

    try {
      setSavingSearch(true);
      const searchCriteria = { ...nonEmptyFilters };

      Object.keys(searchCriteria).forEach((key) => {
        if (Array.isArray(searchCriteria[key])) {
          searchCriteria[key] = searchCriteria[key].map((item) => {
            if (typeof item === 'object' && item !== null && item.value !== undefined) {
              return item.value;
            }
            return item;
          });
        }
      });

      if (searchCriteria.budget) {
        if (Array.isArray(searchCriteria.budget)) {
          searchCriteria.budget = {
            min_value: searchCriteria.budget[0],
            max_value: searchCriteria.budget[1],
          };
        } else if (searchCriteria.budget.min !== undefined || searchCriteria.budget.max !== undefined) {
          searchCriteria.budget = {
            min_value: searchCriteria.budget.min || searchCriteria.budget.min_value,
            max_value: searchCriteria.budget.max || searchCriteria.budget.max_value,
          };
        } else if (
          Array.isArray(searchCriteria.budget) &&
          searchCriteria.budget.length > 0 &&
          typeof searchCriteria.budget[0] === 'object'
        ) {
          searchCriteria.budget = searchCriteria.budget[0];
        }
      }

      const minB = searchCriteria.budget?.min_value ?? searchCriteria.budget?.min;
      const maxB = searchCriteria.budget?.max_value ?? searchCriteria.budget?.max;
      if (searchCriteria.budget_ranges?.length > 0 || (searchCriteria.budget && minB === 0 && maxB === 0)) {
        delete searchCriteria.budget;
      }

      if (searchCriteria.agent_id && Array.isArray(searchCriteria.agent_id)) {
        const agentIds = searchCriteria.agent_id.map((a) => (typeof a === 'object' ? a.id : a));
        searchCriteria.assigned_to = agentIds;
        delete searchCriteria.agent_id;
      }
      if (searchCriteria.agents) delete searchCriteria.agents;

      const searchData = {
        name: searchName.trim(),
        search_type: 'lead',
        search_criteria: searchCriteria,
      };

      const saved_obj = await createSavedSearch(searchData);
      setStatusCounts((prev) => {
        if (!prev) return prev;
        const list = prev.saved_searches || [];
        return { ...prev, saved_searches: [...list, saved_obj] };
      });
      openSuccessModal?.('Success', 'Search saved successfully');
      onSaved?.(filters);
      onClose?.();
    } catch (err) {
      console.error('Error saving search:', err);
      if (err.response?.data) {
        const errorMessage = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v}`)
          .join('\n');
        setError(errorMessage || 'Failed to save search. Please try again.');
      } else if (err.request) {
        setError('No response from server. Please check your connection.');
      } else {
        setError(err.message ? `Error: ${err.message}` : 'Failed to save search.');
      }
    } finally {
      setSavingSearch(false);
    }
  }, [searchName, filters, createSavedSearch, setStatusCounts, openSuccessModal, onClose]);

  return (
    <Dialog
      open={open && Boolean(filters)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '14px', border: `1px solid ${T.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '0.85rem',
          fontWeight: 700,
          color: T.ink,
          borderBottom: `1px solid ${T.border}`,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BookmarkBorderIcon sx={{ fontSize: 14, color: '#fff' }} />
        </Box>
        Save search
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          margin="dense"
          label="Search name"
          placeholder="e.g. Hot leads in Noida"
          fullWidth
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          size="small"
          sx={textFieldStyles}
        />
        {error && (
          <Typography color="error" sx={{ mt: 0.75, fontSize: '0.7rem', whiteSpace: 'pre-line' }}>
            {error}
          </Typography>
        )}
        <SaveSearchFilterPreview previewSrc={filters} defaultFilters={defaultFilters} agents={agents} />
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${T.border}`, gap: 0.75 }}>
        <Button
          onClick={onClose}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.72rem',
            minHeight: 30,
            borderColor: T.border,
            color: T.ink2,
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!searchName.trim() || savingSearch}
          sx={{
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.72rem',
            minHeight: 30,
            textTransform: 'none',
            background: `linear-gradient(135deg, ${T.green} 0%, ${T.greenMd} 100%)`,
            boxShadow: '0 2px 8px rgba(10,107,62,0.25)',
            '&:hover': { background: `linear-gradient(135deg, ${T.greenMd} 0%, ${T.green} 100%)` },
          }}
        >
          {savingSearch ? 'Saving...' : 'Save search'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveLeadSearchDialog;
